import { onMounted, ref, useTemplateRef } from 'vue'
import { useMutationObserver, useResizeObserver } from '@vueuse/core'

import type { Ref } from 'vue'

interface ModalDefaultReturn {
  contentRef: Readonly<ReturnType<typeof useTemplateRef<HTMLElement>>>
  refScroll: Ref<boolean>
}

/** Whether the modal's content scrolls: only then do the controls get the line and the shadow that separate them from
 *  the text running under them. */
export const useModalDefault = (): ModalDefaultReturn => {
  const contentRef = useTemplateRef<HTMLElement>('content')

  const refScroll = ref<boolean>(false)

  // clientHeight, not the content-box size: the content has vertical padding, and measured against its inner height
  // every modal looked scrollable. The 1px margin absorbs subpixel rounding.
  const update = (): void => {
    const element = contentRef.value
    refScroll.value = !!element && element.scrollHeight - element.clientHeight > 1
  }

  useResizeObserver(contentRef, update)
  // At its max height the box keeps its size while what is inside grows (an accordion opens, a list loads).
  useMutationObserver(contentRef, update, { childList: true, subtree: true, characterData: true, attributes: true })

  onMounted(update)

  return {
    contentRef,
    refScroll,
  }
}
